/// <reference types="tom-select" />
import getFormFieldValues from './helpers/getFormFieldValues';
import TomSelect from 'tom-select';
import { createPopper } from '@popperjs/core';

// eslint-disable-next-line import/no-unresolved
import type {
	RecursivePartial,
	TomLoadCallback,
	TomSettings,
} from 'tom-select/dist/types/types';

const $ = window.jQuery;

interface GPAdvancedSelectInitArgs {
	formId: formID;
	fieldId: fieldID;
	lazyLoad: boolean;
	usingSearchValue: boolean;
	hasImageChoices: boolean;
}

interface GPAdvancedSelect extends GPAdvancedSelectInitArgs {}

class GPAdvancedSelect implements GPAdvancedSelectInitArgs {
	public ajaxCache: { [cacheKey: string]: JQuery.jqXHR } = {};

	constructor(initArgs: GPAdvancedSelectInitArgs) {
		const { formId, fieldId, lazyLoad, hasImageChoices } = initArgs;

		this.formId = initArgs.formId;
		this.fieldId = initArgs.fieldId;
		this.lazyLoad = initArgs.lazyLoad;
		this.usingSearchValue = initArgs.usingSearchValue;
		this.hasImageChoices = initArgs.hasImageChoices;

		this.initTomSelect();

		jQuery(document).on('gppa_updated_batch_fields', () => {
			this.initTomSelect();
		});
	}

	public initTomSelect() {
		const selectSelector = `#input_${this.formId}_${this.fieldId}`;
		const $select = $(selectSelector);

		// Prevent double-init or if the selector is not available on current page
		if (
			$select.siblings('.ts-wrapper').length ||
			!$(selectSelector).length
		) {
			return;
		}

		(window as any)[`GPAdvancedSelect_${this.formId}_${this.fieldId}`] =
			new TomSelect(selectSelector, this.tomSelectSettings);
	}

	public get tomSelectSettings(): RecursivePartial<TomSettings> {
		const gpadvs = this;

		const settings: RecursivePartial<TomSettings> = {
			plugins: ['change_listener'],
			valueField: 'id',
			labelField: 'text',
			openOnFocus: true,
			loadThrottle: 0,
			maxOptions: undefined,
			searchField: ['text'],
			onInitialize(this: TomSelect) {
				// Add modifier to prevent the dropdown from going under the Nested Forms/Tingle modal footer.
				const modifiers = [];

				if ($(this.control).closest('.tingle-modal-box').length) {
					modifiers.push({
						name: 'preventOverflow',
						options: {
							boundary: $(this.control).closest(
								'.gform_wrapper'
							)[0],
						},
					});
				}

				this.popper = createPopper(this.control, this.dropdown, {
					modifiers,
				});
			},
			onDropdownOpen(this: TomSelect) {
				this.popper.update();
			},
			onLoad(this: TomSelect) {
				this.popper.update();
			},
		};

		if (this.hasImageChoices) {
			settings.render = {
				// Options in the dropdown
				option(data: any, escape: boolean) {
					if (!data.imageSrc) {
						return `<div>${data.text}</div>`;
					}

					return `<div><img src="${data.imageSrc}" class="gpadvs-image">${data.text}</div>`;
				},
				// The select item
				item(item: any, escape: boolean) {
					if (!item.imageSrc) {
						return `<div>${item.text}</div>`;
					}

					return `<div><img src="${item.imageSrc}" class="gpadvs-image">${item.text}</div>`;
				},
			};
		}

		if (this.lazyLoad) {
			if (this.usingSearchValue) {
				// Disable client-side searching
				settings.searchField = [];
			}

			// Add virtual_scroll (infinite scroll) plugin
			// @ts-ignore
			settings.plugins.push('virtual_scroll');

			// Add first URL callback to determine the first URL (page 1 results)
			settings.firstUrl = (query: string) => {
				return (
					window.GPPA.AJAXURL +
					'?action=gp_advanced_select_get_gppa_results'
				);
			};

			if (this.usingSearchValue) {
				// Require a minimum input length when querying the server
				settings.shouldLoad = (query: string) => {
					return query.length > 2;
				};
			} else {
				settings.shouldLoad = () => true;
			}

			if (!this.usingSearchValue) {
				settings.preload = 'focus';
			}

			// Wire-up the load callback
			settings.load = function (
				query: string,
				callback: TomLoadCallback
			) {
				gpadvs.loadResults.call(this as any, query, callback, gpadvs);
			};
		}

		return window.gform.applyFilters('gpadvs_settings', settings, gpadvs);
	}

	public loadResults(
		this: TomSelect,
		query: string,
		callback: TomLoadCallback,
		gpadvs: GPAdvancedSelect
	) {
		const url = this.getUrl(query);

		const $request = $.ajax({
			url,
			contentType: 'application/json',
			dataType: 'json',
			method: 'POST',
			/*
			 * This should always be false to try to get fresh results from the server. There is a browser
			 * cache in place once fetched for the current page load.
			 */
			cache: false,
			data: JSON.stringify({
				fieldId: gpadvs.fieldId,
				security: window.GPPA.NONCE,
				term: query,
				// The kebab-case of the properties below are already expected by existing methods
				'form-id': gpadvs.formId,
				'field-values': getFormFieldValues(
					gpadvs.formId,
					!!window.gppaForms[gpadvs.formId].gravityViewMeta
				),
			}),
		});

		$request.then((response) => {
			if (response?.pagination?.nextPage) {
				const { nextPage } = response.pagination;

				this.setNextUrl(
					query,
					`${window.GPPA.AJAXURL}?action=gp_advanced_select_get_gppa_results&page=${nextPage}`
				);
			}

			// Types are wacky for the parameters.
			(callback as any)(response.results);
		});

		$request.fail(() => (callback as any)());
	}
}

window.GPAdvancedSelect = GPAdvancedSelect;
